import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, Zap, Brain, Code2, Eye, Clock, Users,
  Heart, Shield, Target, ChevronRight
} from "lucide-react";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import SEOHead from "@/components/SEOHead";

const values = [
  { icon: Zap, title: "Speed Without Compromise", desc: "Fast delivery shouldn't mean cutting corners. We move quickly because we're efficient, not reckless." },
  { icon: Brain, title: "AI-Augmented, Human-Led", desc: "AI is a tool, not a replacement. Critical decisions always have human oversight." },
  { icon: Code2, title: "Technical Excellence", desc: "We're obsessed with code quality, architecture, and performance. No 'good enough.'" },
  { icon: Eye, title: "Radical Transparency", desc: "You'll always know where your project stands, what's working, and what's not." },
  { icon: Heart, title: "Long-Term Thinking", desc: "We build for the long term, not just the launch. Architecture that scales, code that lasts." },
];

const processSteps = [
  {
    week: "Week 1",
    title: "Discovery",
    items: ["Deep dive into your requirements", "Technical architecture proposal", "Timeline & budget estimation", "Risk assessment"],
  },
  {
    week: "Week 2",
    title: "Planning",
    items: ["Detailed project plan", "Milestone definitions", "Tech stack finalization", "Team assignment"],
  },
  {
    week: "Weeks 3-N",
    title: "Delivery",
    items: ["Sprint-based development (2-week sprints)", "Weekly demos & check-ins", "Continuous testing & security", "Daily progress updates"],
  },
  {
    week: "Final Week",
    title: "Launch",
    items: ["Production deployment", "Performance testing", "Security audit", "Documentation handoff"],
  },
  {
    week: "Post-Launch",
    title: "Support",
    items: ["3-month warranty included", "Bug fixes & optimizations", "Optional retainer for ongoing work"],
  },
];

const whatMakesDifferent = [
  {
    num: "01",
    title: "We're AI-Native",
    desc: 'Not "we use AI sometimes." We use Claude, OpenAI, Cursor, and GitHub Copilot extensively in our development process. Every project.',
    details: "AI generates boilerplate, tests, documentation. Humans review, refine, and handle complex logic. Result: 3x faster, same quality.",
  },
  {
    num: "02",
    title: "Product Engineers, Not Feature Factories",
    desc: "We challenge requirements, suggest better approaches, and think about scale from day one. We care about business outcomes, not just shipping code.",
    details: null,
  },
  {
    num: "03",
    title: "Built Products, Not Just Features",
    desc: "We've built complete products from scratch. Not just 'added a feature to an existing app.'",
    details: "MVPs, SaaS platforms, trading dashboards, AI automation tools. We know what it takes to go from zero to production. Backend, frontend, deployment, monitoring - the whole stack.",
  },
];

export default function AboutPage() {
  return (
    <main data-testid="about-page">
      <SEOHead
        title="About Venury | AI-Augmented Engineering Team"
        description="Meet the team behind Venury. Engineers, designers, and product thinkers building production-grade products at startup speed using AI-augmented workflows."
        url="https://venury.tech/about"
      />
      {/* ==================== HERO ==================== */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <div className="hero-orb-teal" style={{ bottom: "0", right: "10%" }} />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `url("https://images.unsplash.com/photo-1550439062-609e1531270e?w=1600&q=40")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1116]/40 to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="tag-box font-mono text-sm text-teal-bright uppercase tracking-[0.2em] mb-6"
          >
            About Venury
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0, 0, 0.2, 1] }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tight max-w-4xl leading-[1.05]"
          >
            Building Products <span className="gradient-text-teal">That Matter</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl mt-8"
          >
            We're engineers, designers, and product thinkers who believe AI should
            amplify human creativity, not replace it.
          </motion.p>
        </div>
      </section>

      {/* ==================== OUR STORY ==================== */}
      <section data-testid="story-section" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-5">
              <ScrollReveal>
                <span className="font-mono text-sm text-orange-bright uppercase tracking-widest block mb-4">Our Story</span>
                <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
                  Why Venury Exists
                </h2>
              </ScrollReveal>
            </div>

            <div className="lg:col-span-7">
              <ScrollReveal delay={0.2}>
                <div className="space-y-6">
                  <p className="text-base text-slate-400 leading-relaxed">
                    In 2025, we saw two concerning trends:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="glass-card rounded-lg p-6">
                      <div className="font-mono text-xs text-red-400 uppercase tracking-widest mb-3">Problem 1</div>
                      <p className="text-sm text-slate-400 leading-relaxed">
                        <span className="text-white font-semibold">Startups burning runway</span> waiting 6+ months for agencies to deliver MVPs that should take weeks.
                      </p>
                    </div>
                    <div className="glass-card rounded-lg p-6">
                      <div className="font-mono text-xs text-red-400 uppercase tracking-widest mb-3">Problem 2</div>
                      <p className="text-sm text-slate-400 leading-relaxed">
                        Agencies slapping <span className="text-white font-semibold">'AI-powered'</span> on their marketing while shipping the same slow, manual work.
                      </p>
                    </div>
                  </div>
                  <p className="text-base text-slate-400 leading-relaxed">Venury exists to close this gap — AI-augmented development that actually delivers faster, not just claims to.</p>

                  <div className="glass-card rounded-lg p-6 border-l-2 border-l-teal-primary">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <Target size={16} className="text-teal-bright mt-1 flex-shrink-0" />
                        <div>
                          <span className="text-white font-semibold text-sm">Mission:</span>
                          <span className="text-sm text-slate-400 ml-2">Deliver production-grade products at startup speed using AI-augmented engineering workflows.</span>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Shield size={16} className="text-teal-bright mt-1 flex-shrink-0" />
                        <div>
                          <span className="text-white font-semibold text-sm">Approach:</span>
                          <span className="text-sm text-slate-400 ml-2">AI handles boilerplate, testing scaffolds, and documentation. Engineers handle architecture, business logic, and critical decisions.</span>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Zap size={16} className="text-orange-bright mt-1 flex-shrink-0" />
                        <div>
                          <span className="text-white font-semibold text-sm">Result:</span>
                          <span className="text-sm text-slate-400 ml-2">Faster delivery. Lower cost. Same production-grade quality.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== WHAT MAKES US DIFFERENT ==================== */}
      <section data-testid="different-section" className="py-20 md:py-32 bg-[#080C10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Why Us</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-16">
              What Makes Us Different
            </h2>
          </ScrollReveal>

          <StaggerContainer className="space-y-6" staggerDelay={0.15}>
            {whatMakesDifferent.map((item, i) => (
              <StaggerItem key={i}>
                <div
                  data-testid={`different-${i}`}
                  className="glass-card rounded-lg p-8 group hover:border-teal-primary/30 transition-colors duration-300"
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <span className="font-mono text-4xl font-bold text-teal-primary/20 group-hover:text-teal-primary/40 transition-colors duration-300">
                      {item.num}
                    </span>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-bold text-white mb-3">{item.title}</h3>
                      <p className="text-base text-slate-400 leading-relaxed">{item.desc}</p>
                      {item.details && (
                        <p className="text-sm text-slate-400 leading-relaxed mt-3 pl-4 border-l-2 border-teal-primary/20">
                          {item.details}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ==================== VALUES ==================== */}
      <section data-testid="values-section" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-orange-bright uppercase tracking-widest block mb-4">Principles</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-16">
              Our Values
            </h2>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.1}>
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <StaggerItem key={i}>
                  <div data-testid={`value-${i}`} className="p-6 rounded-lg border border-white/5 hover:border-teal-primary/20 transition-colors duration-300 h-full">
                    <Icon size={24} className="text-teal-bright mb-4" />
                    <h3 className="font-heading text-lg font-semibold text-white mb-2">{v.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{v.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* ==================== TEAM ==================== */}
      <section data-testid="team-section" className="py-20 md:py-32 bg-[#080C10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <ScrollReveal>
              <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Our Team</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-8">
                Our Background
              </h2>
              <p className="text-base text-slate-400 leading-relaxed mb-6">
                We let our work speak for itself. Here's what our team brings to the table.
              </p>
              <div className="relative h-64 md:h-80 rounded-lg overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1550439062-609e1531270e?w=800&q=80"
                  alt="Team workspace"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#080C10] via-transparent to-transparent" />
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="space-y-6 pt-4 lg:pt-12">
                <div className="glass-card rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Users size={18} className="text-teal-bright" />
                    <h3 className="font-heading text-base font-semibold text-white">Engineering Team</h3>
                  </div>
                  <ul className="space-y-2">
                    <li className="text-sm text-slate-400 flex items-start gap-2">
                      <ChevronRight size={14} className="text-teal-bright mt-0.5 flex-shrink-0" />
                      Deep experience in FinTech backend systems and enterprise architecture
                    </li>
                    <li className="text-sm text-slate-400 flex items-start gap-2">
                      <ChevronRight size={14} className="text-teal-bright mt-0.5 flex-shrink-0" />
                      AI/ML integration specialists working with Claude, OpenAI, and LangChain
                    </li>
                    <li className="text-sm text-slate-400 flex items-start gap-2">
                      <ChevronRight size={14} className="text-teal-bright mt-0.5 flex-shrink-0" />
                      Full-stack capabilities: Java, Spring Boot, React, Node.js, Python, Flutter
                    </li>
                  </ul>
                </div>

                <div className="glass-card rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Target size={18} className="text-orange-bright" />
                    <h3 className="font-heading text-base font-semibold text-white">Specializations</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {["High-frequency trading", "Banking infrastructure", "Compliance automation", "AI/ML integration", "Smart contracts & DeFi"].map((s) => (
                      <span key={s} className="font-mono text-xs px-3 py-1.5 rounded bg-white/5 text-slate-400">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="glass-card rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Eye size={18} className="text-teal-bright" />
                    <h3 className="font-heading text-base font-semibold text-white">Design & Product</h3>
                  </div>
                  <ul className="space-y-2">
                    <li className="text-sm text-slate-400 flex items-start gap-2">
                      <ChevronRight size={14} className="text-teal-bright mt-0.5 flex-shrink-0" />
                      Product thinking, not just UI/UX
                    </li>
                    <li className="text-sm text-slate-400 flex items-start gap-2">
                      <ChevronRight size={14} className="text-teal-bright mt-0.5 flex-shrink-0" />
                      Focus on user experience and business outcomes
                    </li>
                    <li className="text-sm text-slate-400 flex items-start gap-2">
                      <ChevronRight size={14} className="text-teal-bright mt-0.5 flex-shrink-0" />
                      Focus on business outcomes
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ==================== PROCESS ==================== */}
      <section data-testid="process-section" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Process</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-16">
              How We Work
            </h2>
          </ScrollReveal>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-4 md:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-teal-primary via-teal-primary/20 to-transparent hidden md:block" />

            <StaggerContainer className="space-y-8" staggerDelay={0.12}>
              {processSteps.map((step, i) => (
                <StaggerItem key={i}>
                  <div data-testid={`process-step-${i}`} className="flex gap-6 md:gap-10 items-start">
                    {/* Step indicator */}
                    <div className="hidden md:flex flex-col items-center">
                      <div className="w-4 h-4 rounded-full bg-teal-primary flex-shrink-0 relative z-10" />
                    </div>
                    <div className="flex-1 glass-card rounded-lg p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-4">
                        <span className="font-mono text-xs text-teal-bright uppercase tracking-widest">{step.week}</span>
                        <h3 className="font-heading text-xl font-bold text-white">{step.title}</h3>
                      </div>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {step.items.map((item, j) => (
                          <li key={j} className="text-sm text-slate-400 flex items-start gap-2">
                            <ChevronRight size={12} className="text-teal-bright mt-1 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </div>
      </section>
    </main>
  );
}
